/**
 * Хук для работы с сотрудниками
 */

import { useState, useEffect } from 'react';
import employeeService, { Employee, EmployeeSuggestion } from '../services/employeeService';
import cacheService from '../services/cacheService';

interface UseEmployeesOptions {
  restaurantId?: string;
}

export const useEmployees = (options?: UseEmployeesOptions) => {
  const { restaurantId } = options || {};
  const [suggestions, setSuggestions] = useState<EmployeeSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Загружает предложения имен сотрудников с кэшированием
   */
  const fetchEmployeeSuggestions = async (forceRefresh: boolean = false) => {
    if (!restaurantId) {
      setSuggestions([]);
      return { suggestions: [], count: 0 };
    }

    const cacheKey = `employee-suggestions-${restaurantId}`;
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = cacheService.get<EmployeeSuggestion[]>(cacheKey);
      if (cached && cached.length > 0) {
        // Проверяем формат кэша - если это старый формат (строки), игнорируем
        if (typeof cached[0] === 'string') {
          console.log('[useEmployees] Old cache format detected, invalidating...');
          cacheService.set(cacheKey, [], 0); // Очищаем старый кэш
        } else {
          setSuggestions(cached);
          return { suggestions: cached, count: cached.length };
        }
      }
      
      // Skip if we recently got empty results
      if (cacheService.shouldSkipEmptyRequest(cacheKey, 60000)) { // 1 minute threshold
        console.log('Skipping employee suggestions fetch - recent empty response');
        return { suggestions: [], count: 0 };
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await employeeService.getEmployeeSuggestions(restaurantId);
      console.log('[useEmployees] Received from API:', data.suggestions.length, 'suggestions, sample:', data.suggestions[0]);
      setSuggestions(data.suggestions);
      
      // Cache the results
      cacheService.set(cacheKey, data.suggestions, 10 * 60 * 1000); // 10 minutes
      
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка загрузки сотрудников';
      console.warn('Ошибка загрузки сотрудников:', errorMessage);
      
      // Если это ошибка API (например, 429), показываем пустой список и кэшируем его
      if (errorMessage.includes('429') || errorMessage.includes('Quota exceeded')) {
        setSuggestions([]);
        setError(null);
        // Cache empty result to prevent repeated failed requests
        cacheService.set(cacheKey, [], 2 * 60 * 1000); // 2 minutes
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Получает данные сотрудника по имени
   */
  const getEmployeeData = async (name: string): Promise<Employee | null> => {
    try {
      return await employeeService.getEmployeeByName(name, restaurantId);
    } catch (err) {
      console.error('Ошибка получения данных сотрудника:', err);
      return null;
    }
  };

  /**
   * Фильтрует предложения по поисковому запросу (ищет по имени и должности)
   */
  const filterSuggestions = (query: string): EmployeeSuggestion[] => {
    if (!query.trim()) {
      return suggestions;
    }
    
    const lowerQuery = query.toLowerCase();
    return suggestions.filter((suggestion: EmployeeSuggestion) => 
      suggestion.name.toLowerCase().includes(lowerQuery) ||
      suggestion.position.toLowerCase().includes(lowerQuery)
    );
  };

  /**
   * Очищает ошибку
   */
  const clearError = () => {
    setError(null);
  };

  // Загружаем предложения при изменении restaurantId
  useEffect(() => {
    fetchEmployeeSuggestions();
  }, [restaurantId]);

  return {
    suggestions,
    loading,
    error,
    fetchEmployeeSuggestions,
    getEmployeeData,
    filterSuggestions,
    clearError
  };
};